<template>
  <div>
    <ExpiButton 
      class="mt-1"
      button-style="outline"
      :is-disabled="buttonDisabled"
      :click="openLinkModal"
      label-id="Link zu Reisedetails"
    />

    <Modal
      :show="linkModalIsOpen"
      :close="closeLinkModal"
      size="medium"
    >
      <div class="reise-details-link-modal-wrapper mx-5">
        <div 
          class="row"
        >
          <div class="col color-expinet-darkblue text-center generated-link d-flex align-items-center">
            <div v-if="!!link">
              {{ link }}
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col my-3 text-center">
            Buchungen über diesen Link werden mit Ihrer Agentur verknüpft.
          </div>
        </div>

        <div class="row">
          <div class="col-4">
            <input
              v-model="linkAgencyId"
              class="link-agency-input w-100"
              placeholder="Agentur-Nr."
              @keyup.enter="generateLink"
            >
          </div>
          <div class="col-4">
            <input
              v-model="repeaterNumber"
              class="link-agency-input w-100"
              placeholder="Kunden-Nr."
              @keyup.enter="generateLink"
            >
          </div>
          <div class="col-4">
            <ExpiButton 
              button-style="outline"
              :is-disabled="buttonDisabled"
              :click="generateLink"
              label-id="Link erzeugen"
            />
          </div>
        </div>
      </div>
    </Modal>
  </div>
</template>

<script lang="ts">
import ExpiButton from '@/components/common/ExpiButton.vue'
import { useCabinStore, useCruiseStore, useDetailsStore, useFilterStore, useFlightStore, useUserStore } from '@/store'
import { IDomainList } from '@/store/app/interfaces'
import { convertIBEPriceModel } from '@/store/details/cruise/helper'
import { mapState } from 'pinia'
import { defineComponent } from 'vue'
import { handleError, handleInfo } from '../../../general/errorHandling'
import Modal from '../../common/Modal.vue'

export default defineComponent({
  components: { Modal, ExpiButton },

  props: {
    active: { type: Boolean, default: true },
  },

  data: () => {
    return {
      linkModalIsOpen: false,
      linkAgencyId: '',
      repeaterNumber: '',
      link: '',
    }
  },

  computed: {
    ...mapState(useFilterStore, {
      paxConfig: store => store.paxConfig
    }),
    ...mapState(useDetailsStore, {
      priceModel: store => store.priceModel,
    }),
    ...mapState(useCruiseStore, {
      cruise: store => store.cruise
    }),
    ...mapState(useCabinStore, {
      category: store => store.category,
    }),
    ...mapState(useFlightStore, {
      selectedAidaFlight: store => store.selectedFlight
    }),
    ...mapState(useUserStore, {
      agentLogin: store => store.agentLogin,
    }),

    buttonDisabled() {
      return !this.active && (this.linkAgencyId === '' || this.repeaterNumber === '')
    },
  },

  methods: {
    openLinkModal() {
      this.linkModalIsOpen = true
    },
    closeLinkModal() {
      this.linkModalIsOpen = false
      this.link = ''
    },
    async generateLink() {
      if (this.buttonDisabled || this.cruise === null) {
        return
      }

      const params = {
        adults: this.paxConfig.adults,
        juveniles: this.paxConfig.juveniles,
        children: this.paxConfig.children,
        babies: this.paxConfig.babies,
        cabin: this.category ? this.category.mainID : null,
        airport: '',
        agencyId: this.linkAgencyId,
        aidaSource: 'expinet',
        userType: 'customer',
      }

      if (!!this.selectedAidaFlight?.outboundFlight) {
        params.airport = this.selectedAidaFlight?.outboundFlight.departure.airportId
      }

      const q: string[] = []
      for (const key in params) {
        if(!!key) {
          q.push(key + '=' + params[key as keyof typeof params])
        }
      }
      
      const convertedPriceModel = convertIBEPriceModel(this.priceModel!)
      const queryParams = q.join('&')
      const offerLink = `https://aida.de/finden/${this.cruise.id}/${convertedPriceModel}?${queryParams}`
      this.link = offerLink

      const noteLink = `https://aida.de/finden/${this.cruise.id}/${convertedPriceModel}?cabin=${this.category?.mainID}%airport=${params.airport}%26agencyId=${this.linkAgencyId}`
      const note = {
        agentUsername: this.agentLogin,
        cruiseId: this.cruise.id,
        offerUrl: encodeURIComponent(noteLink),
        priceModel: this.priceModel ?? '',
        repeaterNo: this.repeaterNumber,
      }

      const noteString = `?agentUsername=${note.agentUsername}&cruiseId=${note.cruiseId}&offerUrl=${note.offerUrl}&priceModel=${note.priceModel}&repeaterNo=${note.repeaterNo}`
      
      await this.sendLink(noteString)

      handleInfo('Link erzeugen', {
        cruiseId: this.cruise.id,
        offerUrl: noteLink,
      })
    },
    
    sendLink: async (noteString: string) => {
      const domainlist: IDomainList = {
        prod: 'https://my.aida.de',
        test: 'https://stage.aida.de',
        dev: 'https://stage.aida.de',
      }
      const domain = domainlist[process.env.STAGE as keyof IDomainList]

      const key = 
        (process.env.STAGE === 'prod')
        ? 'dbcec5d23fcaf9c4f69542cdef28f05bb748f1efb18c6c732e035c8ef206bae2'
        : '9960790a2d8ab89ae7e9f8fad6ec0117b31166a090338efdf10e4598c06c427b'

      try {
        const url = domain + '/api/v0/expinet/addnote' + noteString
        const options: RequestInit = {
          cache: 'no-cache',
          method: 'GET',
          mode: 'cors',
          headers: {
            'x-api-key': key
          }
        }
        const request = new Request(url)
        const response = await fetch(request, options)
        const text = await response.text()
        return text
      } catch (error) {
        handleError({error, origin: 'sendLink'})
      }
    },
  },
})
</script>

<style lang="scss" scoped>
@import '../../../scss/expinet.scss';
@import '@gecko-akh/shared-styles/src/styles/AOS.vars.scss';

.reise-details-link-modal-wrapper {
  height: 250px;
}

.generated-link {
  height: 150px;
}

.link-agency-input {
  border: 2px solid $color-expinet-gray;
  color: $color-aida-black;
  font-size: 14px;

  &:focus {
    outline: 0;
    border-color: $color-expinet-blue;
  }
}
</style>
